import React, { useEffect } from 'react'
import { RouteComponentProps } from 'react-router'
import {
  Column,
  useColumnOrder,
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table'
import { Grid } from '@material-ui/core'

import DEFAULT_FILTERS from 'core/table/filters'
import { ExportButton, ExtendedPagedTable, useFiltersFromQuery } from 'core/table/components'
import {
  useConditionalSelectColumn,
  useGetColumnState,
  useTableColumnStyles as useStyles,
} from 'core/table/table-hooks/'

import Loading from 'components/Loading'

import { useAutomatedMessagesColumns, useGetAutomatedMessages } from '../hooks'
import { NotificationBlastSummary } from '../types'
import { useDateRangeFilter } from 'filtering/hooks'
import {
  AUTOMATED_MESSAGES_CSV_HEADERS,
  AUTOMATED_MESSAGES_DATE_RANGE_KEY,
  AUTOMATED_MESSAGES_TABLE_NAME,
  MESSAGES_LIST_DEFAULT_DATE_RANGE,
  getAutomatedMessagesOperatorOptions,
} from './automatedMessagesConfig'

export const AutomatedMessagesClient = ({ location, history }: RouteComponentProps) => {
  const [fetchColumnState, { loading: columnStateLoading, result: columnState }] = useGetColumnState()
  const columnStyles = useStyles()
  const [dateRange] = useDateRangeFilter({
    key: AUTOMATED_MESSAGES_DATE_RANGE_KEY,
    defaultDateRange: MESSAGES_LIST_DEFAULT_DATE_RANGE,
  })

  const { result: messages, loading } = useGetAutomatedMessages(dateRange)
  const columns = useAutomatedMessagesColumns()
  const { filters } = useFiltersFromQuery(location.search, getAutomatedMessagesOperatorOptions)

  const table = useTable(
    {
      data: messages,
      columns: columns as Column<NotificationBlastSummary>[],
      initialState: {
        // @ts-ignore
        filters,
        // @ts-ignore
        globalFilter: '',
      },
      filterTypes: DEFAULT_FILTERS,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    useColumnOrder,
    useConditionalSelectColumn(() => true, columnStyles.selectColumn),
  )

  useEffect(() => {
    fetchColumnState(AUTOMATED_MESSAGES_TABLE_NAME)
  }, [fetchColumnState])

  if (loading || columnStateLoading) {
    return <Loading />
  }

  const selectedRows = table.selectedFlatRows.map((row: any) => row.original)
  const disabled = !selectedRows.length

  return (
    <Grid container>
      <ExtendedPagedTable<NotificationBlastSummary>
        history={history}
        location={location}
        table={table}
        columns={columns}
        columnState={columnState}
        tableName={AUTOMATED_MESSAGES_TABLE_NAME}
        searchPlaceholder="Search Messages"
        buttons={
          <>
            <ExportButton
              disabled={disabled}
              data={selectedRows}
              headers={AUTOMATED_MESSAGES_CSV_HEADERS}
              filename={'automated-messages.csv'}
            />
          </>
        }
        useGlobalSearch
        useDateRange
        customDateKey={AUTOMATED_MESSAGES_DATE_RANGE_KEY}
        defaultDateRange={MESSAGES_LIST_DEFAULT_DATE_RANGE}
      />
    </Grid>
  )
}
